import {
    Checkbox,
    FormControlLabel,
    Grid2,
    IconButton,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { Link } from 'react-router-dom';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { togglePaymentMethod } from '@openapi/payment-methods/payment-methods';
import useNotify from '../../../hooks/useNotify';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { useTranslation } from 'react-i18next';

const PaymentMethodsTable = ({
    rows,
    limit,
    page,
    setParams,
    total,
    loading,
    reloadData,
    sort,
    sortDir,
    setSorting,
}) => {
    const { t } = useTranslation();
    const { can, CAPABILITIES } = useCapabilities();
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const [togglingId, setTogglingId] = useState(null);

    // enable or disable payment method
    const handleTogglePaymentMethod = useCallback(
        async (id, venueId, active) => {
            setTogglingId(id);
            try {
                await togglePaymentMethod(venueId, id, {
                    active,
                });
                notifySuccess(t('paymentmethods.notifications.toggled'));
                reloadData();
            } catch (_error) {
                notifyError(t('paymentmethods.notifications.toggleError'));
            } finally {
                setTogglingId(null);
            }
        },
        [notifyError, notifySuccess, reloadData, t],
    );

    useEffect(() => {
        if (rows?.length === 0 && page > 0) {
            setParams({ limit, page: page - 1 });
        }
    }, [rows, limit, page, setParams]);

    return (
        <Grid2 size={{ xs: 12 }}>
            <TableContainer>
                <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow>
                            <SortableTableCell
                                arrow={sort === 'enabled' ? sortDir : ''}
                                onClick={() => setSorting('enabled')}
                            >
                                {t('paymentmethods.columns.enabled')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'slug' ? sortDir : ''}
                                onClick={() => setSorting('slug')}
                            >
                                {t('paymentmethods.columns.slug')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'name' ? sortDir : ''}
                                onClick={() => setSorting('name')}
                            >
                                {t('paymentmethods.columns.name')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'methodType' ? sortDir : ''}
                                onClick={() => setSorting('methodType')}
                            >
                                {t('paymentmethods.columns.methodType')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'agentOnly' ? sortDir : ''}
                                onClick={() => setSorting('agentOnly')}
                            >
                                {t('paymentmethods.columns.agentOnly')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'noteRequired' ? sortDir : ''}
                                onClick={() => setSorting('noteRequired')}
                            >
                                {t('paymentmethods.columns.noteRequired')}
                            </SortableTableCell>
                            <TableCell>{t('paymentmethods.columns.percentage')}</TableCell>
                            <TableCell>{t('paymentmethods.columns.usd')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={limit} cells={9} />
                        ) : (
                            rows?.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={row.enabled}
                                                    onChange={(e) => {
                                                        showModal(MODAL_TYPE.CONFIRMATION, {
                                                            title: t('confirmationModal.title'),
                                                            content: t('confirmationModal.content'),
                                                            confirm: () =>
                                                                handleTogglePaymentMethod(
                                                                    row.id,
                                                                    row.venueId,
                                                                    !!e.target.checked,
                                                                ),
                                                        });
                                                    }}
                                                    disabled={
                                                        !can(CAPABILITIES.PAYMENT_METHODS_MANAGE) ||
                                                        togglingId === row.id
                                                    }
                                                />
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>{row.slug}</TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.methodType}</TableCell>
                                    <TableCell>
                                        <Checkbox checked={row.agentOnly} disabled />
                                    </TableCell>
                                    <TableCell>
                                        <Checkbox checked={row.noteRequired} disabled />
                                    </TableCell>
                                    <TableCell>{row.percentage}%</TableCell>
                                    <TableCell>${row.usd}</TableCell>
                                    <TableCell align="right">
                                        {!can(CAPABILITIES.PAYMENT_METHODS_MANAGE) ? null : (
                                            <Link
                                                to={`/venues/${row.venueId}/paymentmethods/edit/${row.id}`}
                                            >
                                                <Tooltip title={t('common.edit')}>
                                                    <IconButton>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Link>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={rows?.length <= 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Grid2>
    );
};

export default PaymentMethodsTable;
